/* eslint-disable no-bitwise */
// Mimir Index File (mif)
//
// This is a binary file format for containing external index information for MXF files.
// The file contains all the information required to locate frames based on frames numbers,
// but it also contains the required information to write new index table segments in MXF
// subclips. Overview of the information contained in this file:
//

/*
  Summary  of bitstream syntax
  ============================

  ub(n) -- n fixed bits in big-endian of unsigned int, MSB-first order)
  sb(n) -- n fixed bits in big-endian of sign-extended signed int, MSB-first order)

  file:
    "MIF"                              [3 bytes]
    type                               ub(8)
    firstIndexTableOffset              ub(64)
    firstIndexTableSize                ub(64)
    switch (type) {
      case 'F':
        fixedFrameSize                 ub(64)
        firstFrameByteOffset           ub(64)
        break;
      case 'V':
        sliceCount                     ub(8)
        while (!eof) {
          indexEntry                   [15 + 4 * sliceCount bytes]
        }
        break;
    }

  indexEntry:                          [15 + 4 * sliceCount bytes]
    frameByteOffset                    ub(64)
    frameSize                          ub(32)
    randomAccess                       ub(1)
    frameType                          ub(7)
    temporalOffset                     sb(8)
    keyFrameOffset                     sb(8)
    for (i=0; i < sliceCount; i++) {
      sliceOffsets[i]                  ub(32)
    }
*/

import { safeArrayAccess } from '@mimir/common/util/safeArrayAccess';
import { invariant } from '../../util/invariant';
import { MultiArrayBuffer } from './multiArrayBuffer';
import { Parser } from './parser';
import { Serializer, ArraySerializerTarget } from './serializer';

// I: I-frame
// p: P-frame
// b: B-frame
// B: B-frame with only backward prediction (opposite of P-frame)
export type FrameType = 'I' | 'p' | 'b' | 'B';

export interface MimirIndexEntry {
  // This is the absolute offset of the frame in the file.
  frameByteOffset: number,
  // Frame size (including potential KLV fill padding)
  frameSize: number,
  // Frame type (I, p, b)
  frameType: FrameType,
  // Random access (I-frame at a closed GOP boundary)
  randomAccess: boolean,
  // Defines how the frame position has been changed when put
  // into decode order.
  temporalOffset: number,
  // Relative position of the require key frame
  keyFrameOffset: number,
  // The number of slices is decided based on the slice count
  // the index file header. Note that the number of
  // sliceOffsets is fixed for the // entire file so that all
  // index entries have the same size. Slices defined the
  // offsets within one frame in where to find the essence for
  // the various streams. For instance if a frame has the
  // following layout:
  //
  //    | Variable-size video packet | Audio1 | Audio2 |
  //                                 ^
  //                                 +--- Slice offset
  //
  // Since the offset of the audio within the a single frame
  // varies from frame to frame we need to include the offset
  // for each index entry. Because of this we split the frame
  // into two slices. N slices require N - 1 slice offsets per
  // frame. This is because slice 0 will always have offset of
  // zero
  sliceOffsets: number[],
}


export type MimirIndexVariableFrameSize = {
  type: 'variable_frame_size'
  entries: MimirIndexEntry[],
};

export type MimirIndexFixedFrameSize = {
  type: 'fixed_frame_size';
  fixedFrameSize: number,
  firstFrameByteOffset: number,
};

export type MimirIndex = MimirIndexVariableFrameSize | MimirIndexFixedFrameSize;

export interface MimirIndexHeader {
  firstIndexTableOffset: number,
  firstIndexTableSize: number,
}

export interface MimirIndexFile {
  header: MimirIndexHeader,
  index: MimirIndex,
}

export function serializeMimirIndexFile(indexFile: MimirIndexFile): Uint8Array {
  const target = new ArraySerializerTarget();
  const serializer = new Serializer(target);

  writeMimirIndexHeader(serializer, indexFile.index.type, indexFile.header);

  switch (indexFile.index.type) {
    case 'fixed_frame_size':
      serializeMimirIndexFileFixedFrameSize(serializer, indexFile.index);
      break;
    case 'variable_frame_size':
      serializeMimirIndexFileVariableFrameSize(serializer, indexFile.index);
      break;
  }

  return target.getArray();
}



export class IncrementalIndexSerializer {
  // Slice count is initialized by the first entry written.
  // All entries are validated against the sliceCount from
  // the first entry written.
  private sliceCount : number | undefined;

  constructor(
    header: MimirIndexHeader,
    private onData: (chunk: Uint8Array) => void,
  ) {
    const target = new ArraySerializerTarget();
    const serializer = new Serializer(target);
    writeMimirIndexHeader(serializer, 'variable_frame_size', header);

    onData(target.getArray());
  }

  public writeEntry(entry: MimirIndexEntry) {
    const target = new ArraySerializerTarget();
    const serializer = new Serializer(target);
    if (this.sliceCount === undefined) {
      this.sliceCount = entry.sliceOffsets.length;
      serializer.writeUInt8(this.sliceCount);
    } else {
      invariant(
        entry.sliceOffsets.length === this.sliceCount,
        'All MimirIndex file entries must have the same number of slice offsets'
      );
    }
    serializeMimirIndexEntry(serializer, entry);
    this.onData(target.getArray());
  }
}

export function parseMimirIndexFile(array: Uint8Array): MimirIndexFile {
  const buffer = new MultiArrayBuffer();
  buffer.push(array);
  const parser = new Parser(buffer);

  const magic = parser.readString(4);

  const firstIndexTableOffset = parser.readUInt64BE();
  const firstIndexTableSize = parser.readUInt64BE();

  const header : MimirIndexHeader = {
    firstIndexTableOffset,
    firstIndexTableSize,
  };

  let index : MimirIndex;
  switch (magic) {
    case MIMIR_INDEX_FILE_FIXED_FRAME_MAGIC:
      index = parseMimirIndexFileFixedFrameSize(parser);
      break;
    case MIMIR_INDEX_FILE_VARIABLE_FRAME_MAGIC:
      index = parseMimirIndexFileVariableFrameSize(parser);
      break;
    default:
      throw new Error('Not a MimirIndexFile');
  }

  return { header, index };
}

export function getFirstFrameOffset(mimirIndexFile: MimirIndexFile): number {
  switch (mimirIndexFile.index.type) {
    case 'fixed_frame_size':
      return mimirIndexFile.index.firstFrameByteOffset;
    case 'variable_frame_size':
      return mimirIndexFile.index.entries[0]?.frameByteOffset ?? 0;
  }
}

export function getLastFrameEndOffset(mimirIndexFile: MimirIndexFile): number | null {
  switch (mimirIndexFile.index.type) {
    case 'fixed_frame_size':
      // We need the total number of frames to calculate this for fixed frame index files.
      return null;
    case 'variable_frame_size':
      const frameCount = mimirIndexFile.index.entries.length;
      if (frameCount === 0) {
        return null;
      }
      const lastEntry = safeArrayAccess(mimirIndexFile.index.entries, frameCount - 1);
      return lastEntry.frameByteOffset + lastEntry.frameSize;
  }
}

// End of public API

function serializeMimirIndexFileFixedFrameSize(
  serializer: Serializer,
  indexFile: MimirIndexFixedFrameSize,
) {
  serializer.writeUInt64BE(indexFile.fixedFrameSize);
  serializer.writeUInt64BE(indexFile.firstFrameByteOffset);
}

function serializeMimirIndexEntry(
  serializer: Serializer,
  entry: MimirIndexEntry,
) {
  serializer.writeUInt64BE(entry.frameByteOffset);
  serializer.writeUInt32BE(entry.frameSize);
  serializer.writeUInt8(entry.frameType.charCodeAt(0) | (entry.randomAccess ? 0x80 : 0x00));
  serializer.writeInt8(entry.temporalOffset);
  serializer.writeInt8(entry.keyFrameOffset);
  for (const sliceOffset of entry.sliceOffsets) {
    serializer.writeUInt32BE(sliceOffset);
  }
}

function serializeMimirIndexFileVariableFrameSize(
  serializer: Serializer,
  indexFile: MimirIndexVariableFrameSize,
) {
  const sliceCount = indexFile.entries[0]!.sliceOffsets.length;
  for (const entry of indexFile.entries) {
    invariant(
      entry.sliceOffsets.length === sliceCount,
      'All MimirIndex file entries must have the same number of slice offsets'
    );
  }
  serializer.writeUInt8(sliceCount);
  for (const indexEntry of indexFile.entries) {
    serializeMimirIndexEntry(serializer, indexEntry);
  }
}

function writeMimirIndexHeader(
  serializer: Serializer,
  type: 'variable_frame_size' | 'fixed_frame_size',
  header: MimirIndexHeader,
) {
  let magic;
  switch (type) {
    case 'fixed_frame_size':
      magic = MIMIR_INDEX_FILE_FIXED_FRAME_MAGIC;
      break;
    case 'variable_frame_size':
      magic = MIMIR_INDEX_FILE_VARIABLE_FRAME_MAGIC;
      break;
  }

  serializer.writeString(magic);
  serializer.writeUInt64BE(header.firstIndexTableOffset);
  serializer.writeUInt64BE(header.firstIndexTableSize);
}

function parseMimirIndexFileFixedFrameSize(parser: Parser): MimirIndexFixedFrameSize {
  const fixedFrameSize = parser.readUInt64BE();
  const firstFrameByteOffset = parser.readUInt64BE();
  return {
    type: 'fixed_frame_size',
    fixedFrameSize,
    firstFrameByteOffset
  };
}

function parseMimirIndexFileVariableFrameSize(parser: Parser): MimirIndexVariableFrameSize {
  const sliceCount = parser.readUInt8();
  const entries : MimirIndexEntry[] = [];
  while (parser.remain() > 0) {
    const frameByteOffset = parser.readUInt64BE();
    const frameSize = parser.readUInt32BE();
    const frameInfoByte = parser.readUInt8();
    const frameType : string = String.fromCharCode(frameInfoByte & 0x7F);
    invariant(
      frameType === 'I' || frameType === 'p' || frameType === 'b' || frameType === 'B',
      `Invalid frame type: ${frameType}`
    );
    const randomAccess = (frameInfoByte & 0x80) != 0;
    const temporalOffset = parser.readInt8();
    const keyFrameOffset = parser.readInt8();
    const sliceOffsets : number[] = [];
    for (let j = 0; j < sliceCount; j++) {
      sliceOffsets.push(parser.readUInt32BE());
    }
    entries.push({ frameByteOffset, frameSize, frameType, randomAccess, temporalOffset, keyFrameOffset, sliceOffsets });
  }
  return { type: 'variable_frame_size', entries };
}

const MIMIR_INDEX_FILE_FIXED_FRAME_MAGIC = 'MIFF';
const MIMIR_INDEX_FILE_VARIABLE_FRAME_MAGIC = 'MIFV';


